import React, { useEffect, useMemo, useState } from "react";
import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";
import jobService from "../../services/JobService";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";


const COLORS = {
  primary: "#3E0F8D",
  secondary: "#9564DD",
  accent: "#E4DA72",
};

const TRAINERS_API = "/api/trainers/all";

function JobMatches() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const selectedTrainerId = params.get("trainerId");
  const [jobs, setJobs] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [trainerLoading, setTrainerLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [skillFilter, setSkillFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [matchFilter, setMatchFilter] = useState("ALL");
  const [onlyMatched, setOnlyMatched] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Expanded jobs
  const [expandedJobs, setExpandedJobs] = useState({});

  // =========================================================
  // HELPERS
  // =========================================================

  const firstValue = (...values) => {
    for (const value of values) {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        return value;
      }
    }
    return null;
  };

  const getLoggedInPartnerId = () => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return null;

    try {
      const user = JSON.parse(storedUser);

      return (
        user?.partnerId ||
        user?.partner?.id ||
        user?.userId ||
        user?.id ||
        null
      );
    } catch (err) {
      console.error("USER PARSE ERROR:", err);
      return null;
    }
  };

  const getJobId = (job) =>
    firstValue(
      job?.jobId,
      job?.job_id,
      job?.id
    );

  const getPostedById = (job) => {
    const postedBy = firstValue(
      job?.postedBy,
      job?.posted_by,
      job?.postedById,
      job?.posted_by_id
    );

    if (
      postedBy &&
      typeof postedBy === "object"
    ) {
      return firstValue(
        postedBy.id,
        postedBy.userId,
        postedBy.partnerId
      );
    }

    return postedBy;
  };

  // =========================================================
  // NORMALIZE JOB
  // =========================================================

  const normalizeJob = (job) => ({
    ...job,

    jobId: getJobId(job),

    postedById: getPostedById(job),

    jobTitle:
      firstValue(
        job?.jobTitle,
        job?.job_title
      ) || "Untitled Job",

    jobDescription:
      firstValue(
        job?.jobDescription,
        job?.job_description
      ) || "",

    location:
      firstValue(
        job?.location
      ) || "Not specified",

    employeeType:
      firstValue(
        job?.employeeType,
        job?.employee_type
      ) || "Not specified",

    experienceRequired:
      firstValue(
        job?.experienceRequired,
        job?.experience_required
      ) || "Not specified",

    salaryMin:
      firstValue(
        job?.salaryMin,
        job?.salary_min
      ),

    salaryMax:
      firstValue(
        job?.salaryMax,
        job?.salary_max
      ),

    skillsRequired:
      firstValue(
        job?.skillsRequired,
        job?.skills_required
      ) || "",

    vacancy:
      firstValue(
        job?.vacancy
      ) ?? 0,

    applicationDeadline:
      firstValue(
        job?.applicationDeadline,
        job?.application_deadline
      ),

    applicationCount:
      firstValue(
        job?.applicationCount,
        job?.application_count,
        job?.applicationsCount,
        job?.applications
      ) ?? 0,

    status:
      firstValue(
        job?.status
      ) || "PENDING",
  });

  const normalizeResponse = (response) => {
    if (Array.isArray(response))
      return response;

    if (Array.isArray(response?.data))
      return response.data;

    if (Array.isArray(response?.content))
      return response.content;

    if (
      Array.isArray(
        response?.data?.content
      )
    ) {
      return response.data.content;
    }

    if (
      Array.isArray(
        response?.trainers
      )
    ) {
      return response.trainers;
    }

    if (
      Array.isArray(
        response?.data?.trainers
      )
    ) {
      return response.data.trainers;
    }

    return [];
  };

  // =========================================================
  // SKILLS
  // =========================================================

  const skillArray = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .flatMap((item) => {
          if (
            typeof item === "object" &&
            item !== null
          ) {
            return [
              firstValue(
                item?.name,
                item?.skillName,
                item?.skill,
                item?.title
              ),
            ];
          }

          return [item];
        })
        .filter(Boolean)
        .flatMap((item) =>
          String(item)
            .split(/[,|;\/\n]+/)
            .map((x) => x.trim())
            .filter(Boolean)
        );
    }

    if (
      typeof value === "object" &&
      value !== null
    ) {
      const skill = firstValue(
        value?.name,
        value?.skillName,
        value?.skill,
        value?.title
      );

      return skill
        ? String(skill)
          .split(/[,|;\/\n]+/)
          .map((x) => x.trim())
          .filter(Boolean)
        : [];
    }

    return String(value)
      .split(/[,|;\/\n]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const normalizeSkill = (skill) =>
    String(skill || "")
      .toLowerCase()
      .trim()
      .replace(/[._-]+/g, " ")
      .replace(/\s+/g, " ");

  const skillsMatch = (
    jobSkill,
    trainerSkill
  ) => {
    const job = normalizeSkill(jobSkill);
    const trainer =
      normalizeSkill(trainerSkill);

    if (!job || !trainer)
      return false;

    if (job === trainer)
      return true;

    if (
      job.length >= 3 &&
      trainer.includes(job)
    ) {
      return true;
    }

    if (
      trainer.length >= 3 &&
      job.includes(trainer)
    ) {
      return true;
    }

    const jobWords =
      job.split(/\s+/);

    const trainerWords =
      trainer.split(/\s+/);

    return jobWords.some(
      (jw) =>
        jw.length >= 3 &&
        trainerWords.some(
          (tw) =>
            tw === jw ||
            tw.includes(jw) ||
            jw.includes(tw)
        )
    );
  };

  // =========================================================
  // TRAINER HELPERS
  // =========================================================

  const getTrainerSkills = (trainer) =>
    skillArray(
      firstValue(
        trainer?.skills,
        trainer?.skill,
        trainer?.trainerSkills,
        trainer?.trainer_skills,
        trainer?.technicalSkills,
        trainer?.technical_skills,
        trainer?.primarySkills,
        trainer?.primary_skills,
        trainer?.expertise,
        trainer?.specialization,
        trainer?.specializations,
        trainer?.technologies,
        trainer?.technology
      )
    );

  const getTrainerId = (trainer) =>
    firstValue(
      trainer?.trainerId,
      trainer?.trainer_id,
      trainer?.id,
      trainer?.userId,
      trainer?.user_id
    );

  const getTrainerName = (trainer) =>
    firstValue(
      trainer?.name,
      trainer?.fullName,
      trainer?.full_name,
      trainer?.trainerName,
      trainer?.trainer_name,
      trainer?.firstName
        ? `${trainer.firstName} ${trainer.lastName || ""
        }`
        : null
    ) || "Unnamed Trainer";

  const getTrainerEmail = (trainer) =>
    firstValue(
      trainer?.email,
      trainer?.trainerEmail,
      trainer?.trainer_email
    ) || "Not available";

  const getTrainerLocation = (trainer) =>
    firstValue(
      trainer?.location,
      trainer?.city,
      trainer?.currentLocation,
      trainer?.current_location
    ) || "Not specified";

  // =========================================================
  // FIND MATCHING TRAINERS
  // ONE SKILL MATCH = SUGGEST
  // =========================================================

  const findMatchingTrainers = (
    job,
    trainerList
  ) => {
    const jobSkills = skillArray(
      job.skillsRequired
    );

    if (
      jobSkills.length === 0 ||
      trainerList.length === 0
    ) {
      return [];
    }

    const result = [];

    trainerList.forEach((trainer) => {
      const trainerSkills =
        getTrainerSkills(trainer);

      if (!trainerSkills.length)
        return;

      const matchedSkills = [];

      jobSkills.forEach(
        (jobSkill) => {
          const trainerSkill =
            trainerSkills.find(
              (ts) =>
                skillsMatch(
                  jobSkill,
                  ts
                )
            );

          if (
            trainerSkill &&
            !matchedSkills.some(
              (m) =>
                normalizeSkill(
                  m.jobSkill
                ) ===
                normalizeSkill(
                  jobSkill
                )
            )
          ) {
            matchedSkills.push({
              jobSkill,
              trainerSkill,
            });
          }
        }
      );

      if (
        matchedSkills.length > 0
      ) {
        result.push({
          ...trainer,

          trainerId:
            getTrainerId(trainer),

          trainerName:
            getTrainerName(trainer),

          trainerEmail:
            getTrainerEmail(trainer),

          trainerLocation:
            getTrainerLocation(trainer),

          trainerSkills,

          matchedSkills,

          matchPercentage:
            Math.round(
              (matchedSkills.length /
                jobSkills.length) *
              100
            ),
        });
      }
    });

    return result.sort(
      (a, b) =>
        b.matchPercentage -
        a.matchPercentage
    );
  };
 useEffect(() => {
  if (!selectedTrainerId || trainerLoading) return;

  const timer = setTimeout(() => {
    const card = document.getElementById(
      `trainer-card-${selectedTrainerId}`
    );

    if (card) {
      card.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 300);

  return () => clearTimeout(timer);
}, [selectedTrainerId, trainerLoading]);
  // =========================================================
  // FETCH JOBS
  // =========================================================

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError("");

      const partnerId =
        getLoggedInPartnerId();

      if (!partnerId) {
        setError(
          "Partner information not found. Please login again."
        );
        setJobs([]);
        return;
      }

      const response =
        await jobService.getAllJobs();

      const allJobs =
        normalizeResponse(response)
          .map(normalizeJob);

      const partnerJobs =
        allJobs.filter(
          (job) =>
            Number(job.postedById) ===
            Number(partnerId)
        );

      setJobs(partnerJobs);
    } catch (err) {
      console.error(
        "JOB MATCHES ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load jobs."
      );

      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FETCH TRAINERS
  // =========================================================

  const fetchTrainers = async () => {
    try {
      setTrainerLoading(true);

      const response =
        await fetch(
          TRAINERS_API
        );

      if (!response.ok) {
        throw new Error(
          `Trainer API failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      setTrainers(
        normalizeResponse(data)
      );
    } catch (err) {
      console.error(
        "TRAINER API ERROR:",
        err
      );

      setTrainers([]);
    } finally {
      setTrainerLoading(false);
    }
  };

  // =========================================================
  // LOAD
  // =========================================================

  useEffect(() => {
    fetchJobs();
    fetchTrainers();
  }, []);

  // =========================================================
  // JOBS + MATCHING TRAINERS
  // =========================================================

  const jobsWithMatches =
    useMemo(() => {
      return jobs.map((job) => ({
        ...job,

        matchingTrainers:
          findMatchingTrainers(
            job,
            trainers
          ),
      }));
    }, [jobs, trainers]);

  // =========================================================
  // FILTER OPTIONS
  // =========================================================

  const allSkills =
    useMemo(() => {
      const set = new Set();

      jobs.forEach((job) => {
        skillArray(
          job.skillsRequired
        ).forEach((skill) =>
          set.add(skill)
        );
      });

      return [...set].sort();
    }, [jobs]);

  const allLocations =
    useMemo(() => {
      const set = new Set();

      jobs.forEach((job) => {
        if (job.location)
          set.add(job.location);
      });

      return [...set].sort();
    }, [jobs]);

  // =========================================================
  // FILTER JOBS
  // =========================================================

  const filteredJobs =
    useMemo(() => {
      const searchText =
        search
          .toLowerCase()
          .trim();

      return jobsWithMatches.filter(
        (job) => {
          const jobSkills =
            skillArray(
              job.skillsRequired
            );

          const searchableText =
            [
              job.jobTitle,
              job.location,
              job.employeeType,
              job.experienceRequired,
              ...jobSkills,
              ...job.matchingTrainers.map(
                (t) =>
                  t.trainerName
              ),
            ]
              .join(" ")
              .toLowerCase();

          const searchMatch =
            !searchText ||
            searchableText.includes(
              searchText
            );

          const skillMatch =
            skillFilter === "ALL" ||
            jobSkills.some(
              (skill) =>
                normalizeSkill(
                  skill
                ) ===
                normalizeSkill(
                  skillFilter
                )
            );

          const locationMatch =
            locationFilter ===
            "ALL" ||
            normalizeSkill(
              job.location
            ) ===
            normalizeSkill(
              locationFilter
            );

          const matchValue =
            job.matchingTrainers
              .length > 0
              ? Math.max(
                ...job.matchingTrainers.map(
                  (t) =>
                    t.matchPercentage
                )
              )
              : 0;

          let matchFilterResult =
            true;

          if (
            matchFilter === "80"
          ) {
            matchFilterResult =
              matchValue >= 80;
          }

          if (
            matchFilter === "50"
          ) {
            matchFilterResult =
              matchValue >= 50;
          }

          if (
            matchFilter === "1"
          ) {
            matchFilterResult =
              job.matchingTrainers
                .length > 0;
          }

          const trainerMatch =
            !onlyMatched ||
            job.matchingTrainers
              .length > 0;

          return (
            searchMatch &&
            skillMatch &&
            locationMatch &&
            matchFilterResult &&
            trainerMatch
          );
        }
      );
    }, [
      jobsWithMatches,
      search,
      skillFilter,
      locationFilter,
      matchFilter,
      onlyMatched,
    ]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredJobs.length /
        itemsPerPage
      )
    );

  const paginatedJobs =
    filteredJobs.slice(
      (currentPage - 1) *
      itemsPerPage,
      currentPage *
      itemsPerPage
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    skillFilter,
    locationFilter,
    matchFilter,
    onlyMatched,
    itemsPerPage,
  ]);

  // =========================================================
  // CLEAR FILTERS
  // =========================================================

  const clearFilters = () => {
    setSearch("");
    setSkillFilter("ALL");
    setLocationFilter("ALL");
    setMatchFilter("ALL");
    setOnlyMatched(false);
    setCurrentPage(1);
  };

  // =========================================================
  // FORMAT SALARY
  // =========================================================

  const formatSalary = (
    min,
    max
  ) => {
    const hasMin =
      min !== null &&
      min !== undefined &&
      min !== "";

    const hasMax =
      max !== null &&
      max !== undefined &&
      max !== "";

    if (!hasMin && !hasMax)
      return "Not specified";

    const format = (value) =>
      Number(value).toLocaleString(
        "en-IN"
      );

    if (hasMin && hasMax)
      return `₹${format(
        min
      )} - ₹${format(max)}`;

    if (hasMin)
      return `₹${format(min)}+`;

    return `Up to ₹${format(max)}`;
  };

  const formatDate = (value) => {
    if (!value)
      return "Not specified";

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return String(value);
    }

    return date.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  // =========================================================
  // TOGGLE
  // =========================================================

  const toggleJob = (id) => {
    setExpandedJobs(
      (prev) => ({
        ...prev,
        [id]: !prev[id],
      })
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <PartnerHeader />

      <div className="page">

        {/* HEADER */}
        <div className="header">

          <div>
            <h1>
              Job Matches
            </h1>

            <p>
              Find trainers by matching
              job skills
            </p>
          </div>

          <div className="header-stats">

            <div className="stat">
              <b>
                {jobs.length}
              </b>
              <span>Jobs</span>
            </div>

            <div className="stat">
              <b>
                {trainers.length}
              </b>
              <span>Trainers</span>
            </div>

            <div className="stat">
              <b>
                {
                  jobsWithMatches.reduce(
                    (sum, job) =>
                      sum +
                      job.matchingTrainers
                        .length,
                    0
                  )
                }
              </b>
              <span>Matches</span>
            </div>

          </div>

          <button
            className="refresh"
            onClick={() => {
              fetchJobs();
              fetchTrainers();
            }}
          >
            ↻ Refresh
          </button>

        </div>

        {/* FILTER BAR */}
        <div className="filter-box">

          <div className="search-wrap">
            🔎
            <input
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              placeholder="Search job, skill or trainer..."
            />
          </div>

          <select
            value={skillFilter}
            onChange={(e) =>
              setSkillFilter(
                e.target.value
              )
            }
          >
            <option value="ALL">
              All Skills
            </option>

            {allSkills.map(
              (skill) => (
                <option
                  key={skill}
                  value={skill}
                >
                  {skill}
                </option>
              )
            )}
          </select>

          <select
            value={locationFilter}
            onChange={(e) =>
              setLocationFilter(
                e.target.value
              )
            }
          >
            <option value="ALL">
              All Locations
            </option>

            {allLocations.map(
              (location) => (
                <option
                  key={location}
                  value={location}
                >
                  {location}
                </option>
              )
            )}
          </select>

          <select
            value={matchFilter}
            onChange={(e) =>
              setMatchFilter(
                e.target.value
              )
            }
          >
            <option value="ALL">
              Any Match
            </option>

            <option value="80">
              80%+ Match
            </option>

            <option value="50">
              50%+ Match
            </option>

            <option value="1">
              Has Trainer
            </option>
          </select>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={onlyMatched}
              onChange={(e) =>
                setOnlyMatched(
                  e.target.checked
                )
              }
            />
            Matched Only
          </label>

          <button
            className="clear"
            onClick={
              clearFilters
            }
          >
            Clear
          </button>

        </div>

        {/* RESULT BAR */}
        <div className="result-bar">

          <span>
            Showing{" "}
            <b>
              {filteredJobs.length}
            </b>{" "}
            jobs
          </span>

          <select
            value={itemsPerPage}
            onChange={(e) =>
              setItemsPerPage(
                Number(
                  e.target.value
                )
              )
            }
          >
            <option value={5}>
              5 / page
            </option>

            <option value={10}>
              10 / page
            </option>

            <option value={20}>
              20 / page
            </option>
          </select>

        </div>

        {/* LOADING */}
        {(loading ||
          trainerLoading) && (
            <div className="message">
              <div className="loader" />
              Loading jobs and trainers...
            </div>
          )}

        {/* ERROR */}
        {error && (
          <div className="error">
            {error}
            <button
              onClick={fetchJobs}
            >
              Retry
            </button>
          </div>
        )}

        {/* EMPTY */}
        {!loading &&
          !trainerLoading &&
          !error &&
          filteredJobs.length ===
          0 && (
            <div className="empty">
              <div>🔎</div>
              <h3>
                No matching jobs
              </h3>
              <p>
                Try changing your
                filters.
              </p>
            </div>
          )}

        {/* JOBS */}
        {!loading &&
          !trainerLoading &&
          paginatedJobs.length >
          0 && (

            <div className="jobs">

              {paginatedJobs.map(
                (job) => {

                  const trainersForJob =
                    job.matchingTrainers ||
                    [];

                  const expanded =
                    expandedJobs[
                    job.jobId
                    ];

                  const maxMatch =
                    trainersForJob.length
                      ? Math.max(
                        ...trainersForJob.map(
                          (t) =>
                            t.matchPercentage
                        )
                      )
                      : 0;

                  return (
                    <div
                      className="job"
                      key={job.jobId}
                    >

                      {/* COMPACT JOB HEADER */}
                      <div className="job-main">

                        <div className="job-logo">
                          💼
                        </div>

                        <div className="job-name">

                          <h3>
                            {job.jobTitle}
                          </h3>

                          <span>
                            #{job.jobId} ·{" "}
                            {job.location} ·{" "}
                            {job.employeeType}
                          </span>

                        </div>

                        <div className="job-skills">

                          {skillArray(
                            job.skillsRequired
                          )
                            .slice(0, 4)
                            .map(
                              (
                                skill,
                                i
                              ) => (
                                <span
                                  key={i}
                                >
                                  {skill}
                                </span>
                              )
                            )}

                          {skillArray(
                            job.skillsRequired
                          ).length >
                            4 && (
                              <small>
                                +
                                {skillArray(
                                  job.skillsRequired
                                ).length -
                                  4}
                              </small>
                            )}

                        </div>

                        <div className="job-match">

                          <strong>
                            {
                              trainersForJob.length
                            }
                          </strong>

                          <span>
                            Trainers
                          </span>

                        </div>

                        {maxMatch >
                          0 && (
                            <div className="percent">
                              {maxMatch}%
                            </div>
                          )}

                        <button
                          className="expand"
                          onClick={() =>
                            toggleJob(
                              job.jobId
                            )
                          }
                        >
                          {expanded
                            ? "▲"
                            : "▼"}
                        </button>

                      </div>

                      {/* COMPACT EXTRA INFO */}
                      <div className="job-extra">

                        <span>
                          💰{" "}
                          {formatSalary(
                            job.salaryMin,
                            job.salaryMax
                          )}
                        </span>

                        <span>
                          ⏱{" "}
                          {
                            job.experienceRequired
                          }
                        </span>

                        <span>
                          👥{" "}
                          {job.vacancy}
                        </span>

                        <span>
                          📅{" "}
                          {formatDate(
                            job.applicationDeadline
                          )}
                        </span>

                        <span>
                          📋{" "}
                          {
                            job.applicationCount
                          }{" "}
                          Applications
                        </span>

                      </div>

                      {/* TRAINERS */}
                      {expanded && (
                        <div className="trainer-area">

                          <div className="trainer-heading">

                            <b>
                              Suggested Trainers
                            </b>

                            <span>
                              {
                                trainersForJob.length
                              }{" "}
                              matched
                            </span>

                          </div>

                          {trainersForJob.length ===
                            0 ? (
                            <div className="no-trainer">
                              No trainer
                              found with
                              matching
                              skills.
                            </div>
                          ) : (
                            <div className="trainer-table">

                              {trainersForJob.map(
                                (
                                  trainer,
                                  index
                                ) => (

                                 <div
  id={`trainer-card-${trainer.trainerId}`}
  className={`trainer-row ${
    String(trainer.trainerId) === String(selectedTrainerId)
      ? "selected-trainer-card"
      : ""
  }`}
  key={trainer.trainerId || index}
>

                                    <div className="avatar">
                                      {trainer.trainerName
                                        .charAt(
                                          0
                                        )
                                        .toUpperCase()}
                                    </div>

                                    <div className="trainer-name">
                                      <b>
                                        {
                                          trainer.trainerName
                                        }
                                      </b>

                                      <small>
                                        {
                                          trainer.trainerEmail
                                        }
                                      </small>
                                    </div>

                                    <div className="trainer-location">
                                      📍{" "}
                                      {
                                        trainer.trainerLocation
                                      }
                                    </div>

                                    <div className="matched">

                                      {trainer.matchedSkills
                                        .slice(
                                          0,
                                          3
                                        )
                                        .map(
                                          (
                                            match,
                                            i
                                          ) => (
                                            <span
                                              key={
                                                i
                                              }
                                            >
                                              ✓{" "}
                                              {
                                                match.jobSkill
                                              }
                                            </span>
                                          )
                                        )}

                                      {trainer.matchedSkills
                                        .length >
                                        3 && (
                                          <small>
                                            +
                                            {trainer.matchedSkills
                                              .length -
                                              3}
                                          </small>
                                        )}

                                    </div>

                                    <div
                                      className={
                                        trainer.matchPercentage >=
                                          80
                                          ? "match high"
                                          : trainer.matchPercentage >=
                                            50
                                            ? "match medium"
                                            : "match low"
                                      }
                                    >
                                      {
                                        trainer.matchPercentage
                                      }
                                      %
                                    </div>

                                 <button
  type="button"
  className="view"
  onClick={() => {
    const trainerId =
      trainer.trainerId ??
      trainer.id ??
      trainer.trainer_id;

    if (!trainerId) {
      alert("Trainer ID is missing.");
      return;
    }

    navigate(
      `/partner/search-trainers?trainerId=${encodeURIComponent(trainerId)}`
    );
  }}
>
  View Profile
</button>
                                  </div>

                                )
                              )}

                            </div>
                          )}

                        </div>
                      )}

                    </div>
                  );
                }
              )}

            </div>
          )}

        {/* PAGINATION */}
        {!loading &&
          !trainerLoading &&
          filteredJobs.length >
          0 && (
            <div className="pagination">

              <button
                disabled={
                  currentPage === 1
                }
                onClick={() =>
                  setCurrentPage(
                    (p) =>
                      p - 1
                  )
                }
              >
                ‹
              </button>

              {Array.from(
                {
                  length:
                    totalPages,
                },
                (_, i) =>
                  i + 1
              ).map((page) => (
                <button
                  key={page}
                  className={
                    currentPage ===
                      page
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setCurrentPage(
                      page
                    )
                  }
                >
                  {page}
                </button>
              ))}

              <button
                disabled={
                  currentPage ===
                  totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (p) =>
                      p + 1
                  )
                }
              >
                ›
              </button>

            </div>
          )}

      </div>

      <PartnerFooter />

  
<style>{`
  * {
    box-sizing: border-box;
  }

  .page {
    background: #f6f7fb;
    min-height: calc(100vh - 120px);
    padding: 18px 22px;
  }

  /* HEADER */
  .header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 14px;
  }

  .header > div:first-child {
    flex: 1;
    min-width: 0;
  }

  .header h1 {
    margin: 0;
    font-size: 23px;
    color: #1e293b;
  }

  .header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
  }

  .header-stats {
    display: flex;
    gap: 8px;
  }

  .stat {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    min-width: 65px;
    padding: 6px 10px;
    text-align: center;
  }

  .stat b {
    display: block;
    color: \${COLORS.primary};
    font-size: 15px;
  }

  .stat span {
    color: #94a3b8;
    font-size: 9px;
  }

  .refresh {
    border: 0;
    background: \${COLORS.primary};
    color: white;
    padding: 9px 13px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
  }

  /* FILTER */
  .filter-box {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 9px;
    padding: 9px;
    display: flex;
    gap: 7px;
    align-items: center;
    margin-bottom: 9px;
  }

  .search-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 0 8px;
    flex: 1.5;
    min-width: 0;
  }

  .search-wrap input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    padding: 8px 3px;
    font-size: 11px;
  }

  .filter-box select {
    border: 1px solid #e2e8f0;
    background: white;
    border-radius: 6px;
    padding: 8px 7px;
    color: #475569;
    font-size: 10px;
    outline: none;
    max-width: 145px;
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    font-size: 10px;
    color: #475569;
  }

  .clear {
    border: 0;
    background: #fee2e2;
    color: #dc2626;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 10px;
    font-weight: 600;
  }

  /* RESULT */
  .result-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin: 7px 1px;
    color: #64748b;
    font-size: 10px;
  }

  .result-bar b {
    color: \${COLORS.primary};
  }

  .result-bar select {
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    background: white;
    padding: 4px 6px;
    font-size: 9px;
  }

  /* JOB */
  .jobs {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .job {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    min-width: 0;
  }

  .job-main {
    min-height: 58px;
    padding: 9px 11px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .job-logo {
    width: 36px;
    height: 36px;
    min-width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #f0eafa;
    font-size: 16px;
  }

  .job-name {
    width: 205px;
    min-width: 160px;
  }

  .job-name h3 {
    margin: 0 0 2px;
    font-size: 13px;
    color: #1e293b;
  }

  .job-name span {
    color: #94a3b8;
    font-size: 9px;
  }

  .job-skills {
    flex: 1;
    min-width: 0;
    display: flex;
    gap: 4px;
    align-items: center;
    flex-wrap: wrap;
  }

  .job-skills span {
    background: #f3eefb;
    color: \${COLORS.primary};
    border-radius: 4px;
    padding: 4px 6px;
    font-size: 9px;
    font-weight: 600;
  }

  .job-skills small {
    color: #94a3b8;
    font-size: 9px;
  }

  .job-match {
    text-align: center;
    min-width: 55px;
  }

  .job-match strong {
    display: block;
    color: \${COLORS.primary};
    font-size: 15px;
  }

  .job-match span {
    color: #94a3b8;
    font-size: 8px;
  }

  .percent {
    background: #dcfce7;
    color: #15803d;
    padding: 5px 7px;
    border-radius: 5px;
    font-size: 9px;
    font-weight: 800;
  }

  .expand {
    border: 0;
    background: #f1f5f9;
    width: 27px;
    height: 27px;
    border-radius: 5px;
    cursor: pointer;
    color: \${COLORS.primary};
    flex-shrink: 0;
  }

  /* JOB EXTRA */
  .job-extra {
    display: flex;
    gap: 18px;
    border-top: 1px solid #f1f5f9;
    padding: 6px 11px;
    color: #64748b;
    font-size: 9px;
    background: #fcfcfd;
  }

  /* TRAINER AREA */
  .trainer-area {
    border-top: 1px solid #e5e7eb;
    padding: 10px 11px;
    background: #fafbff;
    min-width: 0;
  }

  .trainer-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 7px;
  }

  .trainer-heading b {
    color: #334155;
    font-size: 11px;
  }

  .trainer-heading span {
    color: \${COLORS.primary};
    background: #eee7fa;
    padding: 4px 7px;
    border-radius: 10px;
    font-size: 9px;
  }

  .trainer-table {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }

  .trainer-row {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 43px;
    min-width: 0;
    background: white;
    border: 1px solid #edf0f4;
    border-radius: 6px;
    padding: 7px;
    overflow: visible;
  }

  /* Selected trainer highlight */
  .trainer-row.selected-trainer-card {
    border: 2px solid #3E0F8D;
    background: #f3eefb;
    box-shadow: 0 0 0 3px rgba(62, 15, 141, 0.1);
    scroll-margin-top: 100px;
    transition: background 0.2s ease, border 0.2s ease;
  }

  .avatar {
    width: 29px;
    height: 29px;
    min-width: 29px;
    border-radius: 50%;
    background: \${COLORS.primary};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .trainer-name {
    flex: 0 1 170px;
    min-width: 100px;
    display: flex;
    flex-direction: column;
  }

  .trainer-name b {
    font-size: 10px;
    color: #334155;
    overflow-wrap: anywhere;
  }

  .trainer-name small {
    color: #94a3b8;
    font-size: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .trainer-location {
    width: 120px;
    flex-shrink: 0;
    color: #64748b;
    font-size: 9px;
  }

  .matched {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    align-items: center;
  }

  .matched span {
    background: #dcfce7;
    color: #15803d;
    padding: 3px 5px;
    border-radius: 4px;
    font-size: 8px;
    font-weight: 600;
  }

  .matched small {
    color: #94a3b8;
    padding: 3px;
  }

  .match {
    min-width: 38px;
    flex-shrink: 0;
    text-align: center;
    padding: 4px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 800;
  }

  .match.high {
    color: #15803d;
    background: #dcfce7;
  }

  .match.medium {
    color: #b45309;
    background: #fef3c7;
  }

  .match.low {
    color: #64748b;
    background: #f1f5f9;
  }

  /* VIEW PROFILE BUTTON - works with either existing class */
  .view,
  .view-profile-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto !important;
    width: auto;
    min-width: max-content;
    visibility: visible !important;
    opacity: 1 !important;
    border: none;
    background: #3E0F8D;
    color: #FFFFFF;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    position: relative;
    z-index: 5;
  }

  .view:hover,
  .view-profile-btn:hover {
    background: #26065C;
    color: #FFFFFF;
  }

  .view:focus-visible,
  .view-profile-btn:focus-visible {
    outline: 2px solid #9564DD;
    outline-offset: 2px;
  }

  .no-trainer {
    padding: 15px;
    text-align: center;
    color: #94a3b8;
    font-size: 10px;
  }

  /* PAGINATION */
  .pagination {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin: 15px 0;
  }

  .pagination button {
    min-width: 28px;
    height: 28px;
    border: 1px solid #e2e8f0;
    background: white;
    border-radius: 5px;
    cursor: pointer;
    color: #475569;
    font-size: 10px;
  }

  .pagination button.active {
    background: \${COLORS.primary};
    color: white;
    border-color: \${COLORS.primary};
  }

  .pagination button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* MESSAGE */
  .message,
  .empty,
  .error {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 35px;
    text-align: center;
    color: #64748b;
    font-size: 11px;
  }

  .empty div {
    font-size: 28px;
  }

  .empty h3 {
    margin: 8px 0 3px;
    color: #334155;
    font-size: 14px;
  }

  .empty p {
    margin: 0;
  }

  .error {
    color: #dc2626;
    background: #fffafa;
  }

  .error button {
    display: block;
    margin: 8px auto 0;
    border: 0;
    background: \${COLORS.primary};
    color: white;
    padding: 6px 12px;
    border-radius: 5px;
    cursor: pointer;
  }

  .loader {
    width: 22px;
    height: 22px;
    margin: 0 auto 8px;
    border: 2px solid #e2e8f0;
    border-top-color: \${COLORS.primary};
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* MEDIUM SCREENS */
  @media (max-width: 1100px) {
    .header-stats {
      display: none;
    }

    .job-name {
      width: 170px;
      min-width: 130px;
    }

    .trainer-location {
      display: none;
    }
  }

  /* TABLET AND MOBILE */
  @media (max-width: 800px) {
    .page {
      padding: 12px;
    }

    .filter-box {
      flex-wrap: wrap;
    }

    .search-wrap {
      flex: 1 1 100%;
    }

    .filter-box select {
      flex: 1;
      max-width: none;
    }

    .job-main {
      flex-wrap: wrap;
    }

    .job-name {
      flex: 1;
      min-width: 120px;
    }

    .job-skills {
      width: 100%;
      flex: 1 1 100%;
    }

    .job-extra {
      flex-wrap: wrap;
      gap: 8px 15px;
    }

    .trainer-row {
      flex-wrap: wrap;
      align-items: center;
    }

    .trainer-name {
      flex: 1 1 150px;
      min-width: 100px;
    }

    .matched {
      flex: 1 1 100%;
      width: 100%;
      order: 5;
    }

    .view,
    .view-profile-btn {
      display: inline-flex !important;
      margin-left: auto;
    }
  }

  /* SMALL MOBILE */
  @media (max-width: 500px) {
    .header {
      align-items: flex-start;
      gap: 8px;
    }

    .header h1 {
      font-size: 20px;
    }

    .refresh {
      padding: 7px;
      font-size: 9px;
    }

    .job-name {
      min-width: 120px;
    }

    .job-match {
      min-width: 40px;
    }

    .job-extra {
      font-size: 8px;
    }

    .trainer-row {
      gap: 7px;
    }

    .view,
    .view-profile-btn {
      padding: 7px 10px;
      font-size: 10px;
    }
  }
`}</style>

    </>
  );
}

export default JobMatches;